import {Fragment, useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {clientActionsApi} from 'api';
import {Button, FileInput, PredefinedNotifierType} from 'components';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {PreSessionFileSide, PreSessionStep} from 'types';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW;

const DEFAULT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];

type Props = {
    sessionId: string;
    participantId: string;
    step: PreSessionStep;
    skipButton?: React.ReactNode;
};

export const FileUploadStep: React.FC<Props> = ({sessionId, participantId, step, skipButton}) => {
    const {t} = useTranslation();
    const [uploadedSides, setUploadedSides] = useState<PreSessionFileSide[]>([]);
    const [inputRef, setInputRef] = useState<React.RefObject<HTMLInputElement>>(null);
    const [error, setError] = useState<string>(null);

    const sides = useMemo(() => step.file?.sides?.length ? step.file.sides : [PreSessionFileSide.Single], [step]);
    const extensions = useMemo(() => step.file?.extensions?.length ? step.file.extensions : DEFAULT_EXTENSIONS, [step]);
    const accept = useMemo(() => extensions.map((ext) => `.${ext}`).join(','), [extensions]);
    const currentSide = useMemo(
        () => sides.find((side) => !uploadedSides.includes(side)),
        [sides, uploadedSides]
    );

    const {send: submitFile, loading: submitting} = useApiData(
        clientActionsApi.submitPreSessionFile,
        {notificationType: PredefinedNotifierType.SaveError}
    );

    const handleUpload = useCallback(async (_binary: ArrayBuffer, file: File) => {
        if (!currentSide) {
            return;
        }
        setError(null);
        const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onerror = () => reject(reader.error);
            reader.onload = () => resolve(String(reader.result));
            reader.readAsDataURL(file);
        });

        try {
            await submitFile({sessionId, participantId}, {stepId: step.id, side: currentSide, base64});
            setUploadedSides((sent) => [...sent, currentSide]);
        } catch (e: any) {
            // Server-side rules (type, min and max size) are the source of truth — surface what it said.
            setError(e?.response?.data?.message || t(T_KEYS.FILE_UPLOAD.REJECTED));
        }
    }, [currentSide, participantId, sessionId, step.id, submitFile, t]);

    const openPicker = useCallback(() => inputRef?.current?.click(), [inputRef]);

    const sideLabel = useCallback((side: PreSessionFileSide) => {
        if (side === PreSessionFileSide.Front) {
            return t(T_KEYS.FILE_UPLOAD.SIDE_FRONT);
        }
        if (side === PreSessionFileSide.Back) {
            return t(T_KEYS.FILE_UPLOAD.SIDE_BACK);
        }
        return t(T_KEYS.FILE_UPLOAD.SIDE_SINGLE);
    }, [t]);

    return (
        <Fragment>
            <div className='file-upload-step-sides'>
                {sides.map((side) => (
                    <div
                        key={side}
                        className={uploadedSides.includes(side) ? 'side side--done' : 'side'}
                    >
                        {sideLabel(side)}
                        {uploadedSides.includes(side) && <span className='side-check'>{' ✓'}</span>}
                    </div>
                ))}
            </div>

            {!!error && <div className='file-upload-step-error'>{error}</div>}

            <div className='file-upload-step-controls'>
                <Button
                    disabled={submitting || !currentSide}
                    onClick={openPicker}
                >
                    {currentSide
                        ? `${t(T_KEYS.FILE_UPLOAD.UPLOAD)} — ${sideLabel(currentSide)}`
                        : t(T_KEYS.FILE_UPLOAD.DONE)}
                </Button>
                {skipButton}
            </div>

            <div className='file-upload-step-hint'>
                {`${t(T_KEYS.FILE_UPLOAD.ACCEPTED)}: ${extensions.join(', ')}`}
            </div>

            <FileInput accept={accept} setInputRef={setInputRef} onUpload={handleUpload} />
        </Fragment>
    );
};
